<script setup>
// Bottom-sheet header: method icon + label, venue name, close/back button.
// Port of `SheetHeader` in the source `_shared/CheckInSheet.tsx`.
import { computed } from 'vue'
import { X, ArrowLeft, MapPin, Lock, ScanLine, Brain, CircleHelp } from '@lucide/vue'
import { methodMeta } from './checkin'

const props = defineProps({
  spec: { type: Object, required: true },
  backMode: { type: Boolean, default: false },
})

defineEmits(['close', 'back', 'help'])

const icons = { MapPin, Lock, ScanLine, Brain }
const meta = computed(() => methodMeta[props.spec.method] || methodMeta.gps)
const methodIcon = computed(() => icons[meta.value.icon] || MapPin)
</script>

<template>
  <div class="cs-header">
    <div class="cs-header__left">
      <span class="cs-header__ring">
        <component :is="methodIcon" :size="14" />
      </span>
      <div>
        <div class="cs-header__label">{{ meta.label }}</div>
        <div class="cs-header__venue">{{ spec.venueName }}</div>
      </div>
    </div>
    <div class="cs-header__actions">
      <!-- 44px hit area around the 28px visual ring. -->
      <button
        id="checkin-sheet-header-help"
        type="button"
        class="cs-header__btn cs-header__hit gtm-click"
        aria-label="Get help"
        @click="$emit('help')"
      >
        <span class="cs-header__ring cs-header__ring--muted">
          <CircleHelp :size="16" />
        </span>
      </button>
      <button
        id="checkin-sheet-header-dismiss"
        type="button"
        class="cs-header__ring cs-header__btn gtm-click"
        :aria-label="backMode ? 'Back' : 'Close'"
        @click="$emit(backMode ? 'back' : 'close')"
      >
        <ArrowLeft v-if="backMode" :size="16" />
        <X v-else :size="16" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `flex items-center justify-between px-4 pt-3 pb-3 shrink-0`
.cs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px; // px-4 pt-3 pb-3
  flex-shrink: 0;

  &__left {
    display: flex;
    align-items: center;
    gap: 8px; // gap-2
  }

  // Source: `grid h-7 w-7 place-items-center rounded-full ring-1
  //          ring-current/15 ${theme.accent}`
  &__ring {
    @include cs-icon-ring;
    color: var(--fr-accent, var(--fr-primary, #FF9900));

    &--muted {
      color: var(--fr-muted, #6b7280);
    }
  }

  &__actions {
    display: flex;
    align-items: center;
  }

  // 44px tap target wrapping a 28px ring; negative vertical margin keeps the
  // header's visual height unchanged.
  &__hit {
    display: grid;
    height: 44px;
    width: 44px;
    place-items: center;
    margin: -8px -4px -8px -8px;
  }

  &__btn {
    appearance: none;
    border: 0;
    margin: 0;
    padding: 0;
    background: none;
    font: inherit;
    cursor: pointer;
  }

  // Source: `font-display text-[10px] font-bold uppercase tracking-wider ${theme.muted}`
  &__label {
    @include cs-overline(10px);
    color: var(--fr-muted, #6b7280);
  }

  // Source: `font-serif-display text-base font-bold italic leading-tight`
  &__venue {
    font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
    font-size: 16px; // text-base
    font-weight: 700;
    font-style: italic;
    line-height: 1.25; // leading-tight
  }
}
</style>
